import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useGetList, useGetMany, ReferenceField, TextField, useRecordContext, useNotify } from 'react-admin';
import { Funeral, DeliveryAddress } from '../types';
import moment from 'moment';

interface DeliveryAddressesModalProps {
  open: boolean;
  onClose: () => void;
}

const FuneralAddressItem = ({ funeral }: { funeral: Funeral }) => {
  return (
    <Box sx={{ 
      mb: 3, 
      p: 2, 
      backgroundColor: '#f9f9f9', 
      borderRadius: 1,
      position: 'relative',
      '& .RaField-root': {
        margin: 0,
      }
    }}>
      <Box sx={{ width: '100%' }}>
        {/* Deceased Name */}
        <ReferenceField
          record={funeral}
          source="deceased_client_id"
          reference="clients"
          link={false}
          sx={{ display: 'block', width: '100%' }}
        >
          <DeceasedNameDisplay />
        </ReferenceField>

        {/* Delivery Address */}
        {funeral.delivery_address ? (
          <DeliveryAddressDisplay funeral={funeral} />
        ) : (
          <Typography variant="body2" color="text.secondary">
            No delivery address set
          </Typography>
        )}

        {/* Delivery Window */}
        {funeral.delivery_window && (
          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'primary.main', fontWeight: 'medium' }}>
            Window: {funeral.delivery_window}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const DeceasedNameDisplay = () => {
  const client = useRecordContext();
  
  if (!client) return null;

  return (
    <Typography>
      <strong>{client.name}</strong>
    </Typography>
  );
};

const DeliveryAddressDisplay = ({ funeral }: { funeral: Funeral }) => {
  const delivery = funeral.delivery_address;
  if (!delivery) return null;

  // Format the delivery address
  const formatAddress = () => {
    const parts = [];
    
    // Format street address (apartment-civic_number address format)
    const streetParts = [];
    if (delivery.apartment && delivery.civic_number) {
      streetParts.push(`${delivery.apartment}-${delivery.civic_number}`);
    } else if (delivery.apartment) {
      streetParts.push(delivery.apartment);
    } else if (delivery.civic_number) {
      streetParts.push(delivery.civic_number);
    }
    if (delivery.address) {
      streetParts.push(delivery.address);
    }
    if (streetParts.length > 0) {
      parts.push(streetParts.join(' '));
    }
    
    if (delivery.city) parts.push(delivery.city);
    if (delivery.province) parts.push(delivery.province);
    if (delivery.postal_code) parts.push(delivery.postal_code);
    return parts.join(', ');
  };

  return (
    <>
      {delivery.recipient_name && (
        <Typography variant="body1">
          <strong>Attn: {delivery.recipient_name}</strong>
        </Typography>
      )}
      <Typography variant="body2" color="text.secondary">
        {formatAddress()}
      </Typography>
    </>
  );
};

export const DeliveryAddressesModal: React.FC<DeliveryAddressesModalProps> = ({ 
  open, 
  onClose 
}) => {
  const notify = useNotify();
  
  // Get funerals with delivery statuses
  const { data: funerals, isLoading } = useGetList(
    'funerals',
    {
      filter: {
        'cremation_status@in': '(cremation_scheduled,delivery_scheduled,ready_for_delivery)'
      },
      pagination: { page: 1, perPage: 100 },
      sort: { field: 'cremation_date', order: 'ASC' }
    },
    { enabled: open }
  );

  // Get all unique client IDs for deceased and primary clients
  const clientIds = React.useMemo(() => {
    if (!funerals) return [];
    const ids = new Set<string>();
    funerals.forEach(funeral => {
      if (funeral.deceased_client_id) ids.add(funeral.deceased_client_id);
      if (funeral.primary_client_id) ids.add(funeral.primary_client_id);
    });
    return Array.from(ids);
  }, [funerals]);

  // Get client data for copy function
  const { data: clients } = useGetMany(
    'clients',
    { ids: clientIds },
    { enabled: !!funerals && clientIds.length > 0 }
  );

  // Group funerals by delivery day
  const groupedFunerals = React.useMemo(() => {
    if (!funerals) return {};

    const groups: Record<string, Funeral[]> = {};
    
    funerals.forEach((funeral) => {
      let groupKey: string;
      
      if (funeral.delivery_day) {
        // Format date as YYYY-MM-DD for consistent grouping
        groupKey = moment(funeral.delivery_day).format('YYYY-MM-DD');
      } else {
        groupKey = 'no-date';
      }
      
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(funeral);
    });

    return groups;
  }, [funerals]);

  // Sort group keys: dated groups first (chronologically), then 'no-date' group
  const sortedGroupKeys = React.useMemo(() => {
    const keys = Object.keys(groupedFunerals);
    const datedKeys = keys.filter(key => key !== 'no-date').sort();
    const noDatedKeys = keys.filter(key => key === 'no-date');
    return [...datedKeys, ...noDatedKeys];
  }, [groupedFunerals]);

  const formatGroupTitle = (groupKey: string): string => {
    if (groupKey === 'no-date') {
      return 'No Delivery Date Set';
    }
    return moment(groupKey).format('dddd, MMMM D, YYYY');
  };

  // Function to generate copyable text matching the modal display
  const generateCopyText = React.useCallback(() => {
    if (!funerals) return '';

    let copyText = '';

    sortedGroupKeys.forEach((groupKey, index) => {
      if (index > 0) copyText += '\n\n';
      
      copyText += `${formatGroupTitle(groupKey)} (${groupedFunerals[groupKey].length})\n`;
      copyText += '-'.repeat(50) + '\n\n';

      groupedFunerals[groupKey].forEach((funeral) => {
        // Get deceased name from clients lookup
        const deceasedClient = clients?.find(c => c.id === funeral.deceased_client_id);
        const deceasedName = deceasedClient?.name || 'Unknown';
        copyText += `${deceasedName}\n`;
        
        // Get delivery address
        if (funeral.delivery_address) {
          const delivery = funeral.delivery_address;
          
          // Recipient name if set
          if (delivery.recipient_name) {
            copyText += `Attn: ${delivery.recipient_name}\n`;
          }
          
          // Format address (matching streetParts pattern from DeliveryAddressDisplay)
          const addressParts = [];
          const streetParts = [];
          if (delivery.apartment && delivery.civic_number) {
            streetParts.push(`${delivery.apartment}-${delivery.civic_number}`);
          } else if (delivery.apartment) {
            streetParts.push(delivery.apartment);
          } else if (delivery.civic_number) {
            streetParts.push(delivery.civic_number);
          }
          if (delivery.address) {
            streetParts.push(delivery.address);
          }
          if (streetParts.length > 0) {
            addressParts.push(streetParts.join(' '));
          }
          if (delivery.city) addressParts.push(delivery.city);
          if (delivery.province) addressParts.push(delivery.province);
          if (delivery.postal_code) addressParts.push(delivery.postal_code);

          const fullAddress = addressParts.join(', ');
          if (fullAddress) {
            copyText += `${fullAddress}\n`;
          }
        } else {
          copyText += 'No delivery address set\n';
        }
        
        // Delivery window if set
        if (funeral.delivery_window) {
          copyText += `Window: ${funeral.delivery_window}\n`;
        }
        
        copyText += '\n';
      });
    });

    return copyText.trim();
  }, [funerals, clients, sortedGroupKeys, groupedFunerals, formatGroupTitle]);

  const handleCopyAll = () => {
    const textToCopy = generateCopyText();
    navigator.clipboard.writeText(textToCopy).then(() => {
      notify('Delivery addresses copied to clipboard', { type: 'success' });
    }).catch(() => {
      notify('Failed to copy delivery addresses', { type: 'error' });
    });
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { minHeight: '60vh' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 1 }}>
          <Typography variant="h5">Delivery Addresses</Typography>
          <Tooltip title="Copy all delivery addresses">
            <IconButton 
              onClick={handleCopyAll}
              disabled={!funerals || funerals.length === 0}
              sx={{ 
                color: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.light',
                  color: 'white'
                }
              }}
            >
              <ContentCopyIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {isLoading ? (
          <Typography>Loading...</Typography>
        ) : !funerals || funerals.length === 0 ? (
          <Typography>No funerals found for delivery.</Typography>
        ) : (
          <Box>
            {sortedGroupKeys.map((groupKey, groupIndex) => (
              <React.Fragment key={groupKey}>
                {/* Group Header */}
                <Typography 
                  variant="h6" 
                  sx={{ 
                    mt: groupIndex > 0 ? 3 : 0, 
                    mb: 2, 
                    px: 2, 
                    py: 1, 
                    backgroundColor: groupKey === 'no-date' ? '#fff3cd' : '#e3f2fd',
                    borderRadius: 1,
                    fontWeight: 'bold'
                  }}
                >
                  {formatGroupTitle(groupKey)} ({groupedFunerals[groupKey].length})
                </Typography>

                {/* Funerals in this group */}
                {groupedFunerals[groupKey].map((funeral, funeralIndex) => (
                  <React.Fragment key={funeral.id}>
                    <FuneralAddressItem funeral={funeral} />
                    {funeralIndex < groupedFunerals[groupKey].length - 1 && (
                      <Divider sx={{ my: 1 }} />
                    )}
                  </React.Fragment>
                ))}
              </React.Fragment>
            ))}
          </Box>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}; 